/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Edit, Briefcase, Building2, DollarSign,
  Award, Clock, CheckCircle, Copy, Receipt
} from 'lucide-react';
import { usePartner, usePartnerReferrals } from '@/hooks/api/usePartners';
import { usePartnerCommissions } from '@/hooks/api/useCommissions';
import { PartnerStatus, PartnerReferral, Commission } from '@/types/superadmin.types';

// Stat Card
const StatCard: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  color: 'green' | 'blue' | 'purple' | 'orange' | 'red';
}> = ({ icon, value, label, color }) => {
  const colors = {
    green: 'bg-emerald-500/15 text-emerald-500',
    blue: 'bg-blue-500/15 text-blue-500',
    purple: 'bg-purple-500/15 text-purple-500',
    orange: 'bg-orange-500/15 text-orange-500',
    red: 'bg-red-500/15 text-red-500',
  };

  const cardClasses = color === 'red'
    ? 'bg-surface border border-red-500/40 rounded-xl p-4 shadow-[0_0_15px_rgba(239,68,68,0.15)]'
    : 'bg-surface border border-border rounded-xl p-4';

  return (
    <div className={cardClasses}>
      <div className="mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
      <div className={`font-mono text-3xl font-bold ${color === 'red' ? 'text-red-500' : 'text-foreground'}`}>{value}</div>
      <div className="text-sm text-secondary">{label}</div>
    </div>
  );
};

// Detail Item
const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-secondary text-sm">{label}</span>
    <span className="text-foreground text-base font-medium">{value}</span>
  </div>
);

// Main Partner Detail Page
export default function PartnerDetailPage() {
  const { t } = useTranslation('platform');
  const { id } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Fetch partner data from API
  const { data: partner, isLoading: partnerLoading } = usePartner(id || '');
  // Fetch referrals separately
  const { data: referrals, isLoading: referralsLoading } = usePartnerReferrals(id || '');
  // Fetch partner commissions (payments)
  const { data: commissionsData, isLoading: commissionsLoading } = usePartnerCommissions(id || '');
  const commissions = commissionsData?.content || [];

  const loading = partnerLoading;

  const copyInviteCode = () => {
    navigator.clipboard.writeText(partner?.inviteCode || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted">
        <Briefcase className="w-12 h-12 mb-3 opacity-50" />
        <p>{t('partnerDetail.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="partner-detail-header flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/superadmin/partners')}
            className="p-2 rounded-lg border border-border hover:bg-background transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="partner-avatar w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-lg font-bold">
              <Briefcase className="w-6 h-6" />
            </div>
            <div>
              <h1 className="partner-title text-2xl font-bold text-foreground flex items-center gap-2 flex-wrap">
                {partner.name}
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  partner.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' : 'bg-gray-500/15 text-gray-400'
                }`}>
                  {partner.status}
                </span>
              </h1>
              <p className="text-sm text-muted">{partner.company}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate(`/superadmin/partners/${id}/edit`)}
          className="partner-edit-btn flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
        >
          <Edit className="w-4 h-4" /> <span className="btn-text">{t('partnerDetail.editPartner')}</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Building2 className="w-5 h-5" />}
          value={partner.activeReferrals.toString()}
          label={t('partnerDetail.stats.activeReferrals')}
          color="blue"
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5" />}
          value={`$${partner.totalEarnings.toLocaleString()}`}
          label={t('partnerDetail.stats.totalEarnings')}
          color="green"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          value={`$${Math.abs(partner.pendingPayout).toLocaleString()}`}
          label={partner.pendingPayout < 0 ? t('partnerDetail.stats.balanceOwed') : t('partnerDetail.stats.pendingPayout')}
          color={partner.pendingPayout < 0 ? 'red' : 'orange'}
        />
        <StatCard
          icon={<Award className="w-5 h-5" />}
          value={`${partner.commissionRate}%`}
          label={t('partnerDetail.stats.commissionRate')}
          color="purple"
        />
      </div>

      {/* Invite Code Banner */}
      <div className="bg-gradient-to-r from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-xl p-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-muted mb-1">{t('partnerDetail.inviteCode.label')}</div>
          <div className="font-mono text-xl font-bold text-foreground">{partner.inviteCode}</div>
        </div>
        <button
          onClick={copyInviteCode}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
        >
          {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? t('partnerDetail.inviteCode.copied') : t('partnerDetail.inviteCode.copy')}
        </button>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Partner Details */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2 mb-4">
            <Briefcase className="w-4 h-4" /> {t('partnerDetail.sections.partnerDetails')}
          </h3>
          <div className="flex flex-col gap-3">
            <DetailItem label={t('partnerDetail.fields.contactEmail')} value={partner.email} />
            <DetailItem label={t('partnerDetail.fields.phone')} value={partner.phone} />
            <DetailItem label={t('partnerDetail.fields.partnerType')} value={
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                partner.type === 'RESELLER' ? 'bg-purple-500/15 text-purple-500' :
                partner.type === 'REFERRAL' ? 'bg-blue-500/15 text-blue-500' :
                'bg-orange-500/15 text-orange-500'
              }`}>
                {t(`partners.type.${partner.type.toLowerCase()}`)}
              </span>
            } />
            <DetailItem label={t('partnerDetail.fields.joined')} value={new Date(partner.joinedAt).toLocaleDateString()} />
            <DetailItem label={t('partnerDetail.fields.address')} value={partner.address || '-'} />
          </div>
        </div>

        {/* Payout Information */}
        <div className="bg-surface border border-border rounded-xl p-5">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2 mb-4">
            <DollarSign className="w-4 h-4" /> {t('partnerDetail.sections.payoutInfo')}
          </h3>
          <div className="flex flex-col gap-3">
            <DetailItem label={t('partnerDetail.fields.commissionRate')} value={`${partner.commissionRate}%`} />
            <DetailItem
              label={t('partnerDetail.fields.autoApproveCommissions', 'Auto-approve')}
              value={
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                  partner.autoApproveCommissions
                    ? 'bg-emerald-500/15 text-emerald-500'
                    : 'bg-gray-500/15 text-gray-400'
                }`}>
                  {partner.autoApproveCommissions ? (
                    <><CheckCircle className="w-3 h-3" /> {t('common.enabled', 'Enabled')}</>
                  ) : (
                    t('common.disabled', 'Disabled')
                  )}
                </span>
              }
            />
            <DetailItem label={t('partnerDetail.fields.totalEarnings')} value={`$${partner.totalEarnings.toLocaleString()}`} />
            <DetailItem
              label={partner.pendingPayout < 0 ? t('partnerDetail.fields.balanceOwed') : t('partnerDetail.fields.pendingPayout')}
              value={
                <span className={partner.pendingPayout < 0 ? 'text-red-500' : ''}>
                  ${Math.abs(partner.pendingPayout).toLocaleString()}
                </span>
              }
            />
            <DetailItem label={t('partnerDetail.fields.lastPayout')} value={partner.lastPayoutDate ? `$${partner.lastPayoutAmount} ${t('partnerDetail.on')} ${new Date(partner.lastPayoutDate).toLocaleDateString()}` : t('partnerDetail.noPayoutsYet')} />
            <DetailItem label={t('partnerDetail.fields.paymentMethod')} value={partner.bankDetails || t('partnerDetail.notConfigured')} />
          </div>
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
            <Building2 className="w-4 h-4" /> {t('partnerDetail.referrals.title', { count: referrals?.length || 0 })}
          </h3>
        </div>
        <div className="overflow-x-auto">
          {referralsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : referrals && referrals.length > 0 ? (
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.organization')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.plan')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.mrr')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.commission')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.referrals.joined')}</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((ref: PartnerReferral) => (
                  <tr key={ref.id} className="border-b border-border last:border-b-0 hover:bg-background/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{ref.organizationName}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                        ref.plan === 'ENTERPRISE' ? 'bg-red-500/15 text-red-500' :
                        ref.plan === 'PROFESSIONAL' ? 'bg-purple-500/15 text-purple-500' :
                        'bg-gray-500/15 text-gray-400'
                      }`}>
                        {ref.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        ref.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-500' :
                        ref.status === 'TRIAL' ? 'bg-blue-500/15 text-blue-500' :
                        'bg-gray-500/15 text-gray-400'
                      }`}>
                        {ref.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-foreground">${ref.mrr}</td>
                    <td className="px-4 py-3 font-mono text-sm text-emerald-500">${ref.commission}</td>
                    <td className="px-4 py-3 text-sm text-muted">{new Date(ref.joinedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted">
              <Building2 className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">{t('partnerDetail.referrals.empty')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Commissions/Payments Table */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-md font-semibold text-foreground flex items-center gap-2">
            <Receipt className="w-4 h-4" /> {t('partnerDetail.commissions.title', { count: commissions.length, defaultValue: `Commissions (${commissions.length})` })}
          </h3>
        </div>
        <div className="overflow-x-auto">
          {commissionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : commissions.length > 0 ? (
            <table className="w-full">
              <thead className="bg-background">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.organization', 'Organization')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.invoice', 'Invoice')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.baseAmount', 'Base Amount')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.rate', 'Rate')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.commission', 'Commission')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.status', 'Status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-muted">{t('partnerDetail.commissions.date', 'Date')}</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((comm: Commission) => (
                  <tr key={comm.id} className="border-b border-border last:border-b-0 hover:bg-background/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{comm.organizationName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-muted font-mono">{comm.invoiceNumber || '-'}</td>
                    <td className="px-4 py-3 font-mono text-sm text-foreground">${comm.baseAmount?.toFixed(2) || '0.00'}</td>
                    <td className="px-4 py-3 text-sm text-muted">{comm.commissionRate}%</td>
                    <td className="px-4 py-3 font-mono text-sm text-emerald-500">${comm.amount?.toFixed(2) || '0.00'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        comm.status === 'PAID' ? 'bg-emerald-500/15 text-emerald-500' :
                        comm.status === 'APPROVED' ? 'bg-blue-500/15 text-blue-500' :
                        comm.status === 'PENDING' ? 'bg-yellow-500/15 text-yellow-500' :
                        comm.status === 'CANCELLED' ? 'bg-red-500/15 text-red-500' :
                        'bg-gray-500/15 text-gray-400'
                      }`}>
                        {comm.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">{new Date(comm.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-muted">
              <Receipt className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">{t('partnerDetail.commissions.empty', 'No commissions yet')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 768px) {
          .partner-detail-header {
            flex-direction: column;
            align-items: stretch;
          }
          .partner-avatar {
            width: 40px;
            height: 40px;
          }
          .partner-title {
            font-size: 18px;
          }
          .partner-edit-btn {
            width: 100%;
            justify-content: center;
          }
          .partner-edit-btn .btn-text {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
